import { Condition, VisibilityCondition } from '../types';

/**
 * Evaluates if a question should be visible based on the current form data
 */
export const evaluateVisibility = (
  condition: VisibilityCondition | undefined,
  formData: Record<string, any>
): boolean => {
  if (!condition) return true;

  let allPass = true;
  let anyPass = false;

  // Check 'all' conditions - all must be true
  if (condition.all && condition.all.length > 0) {
    for (const cond of condition.all) {
      if (!evaluateSingleCondition(cond, formData)) {
        allPass = false;
        break;
      }
    }
  }

  // Check 'any' conditions - at least one must be true
  if (condition.any && condition.any.length > 0) {
    for (const cond of condition.any) {
      if (evaluateSingleCondition(cond, formData)) {
        anyPass = true;
        break;
      }
    }
  }

  // If both 'all' and 'any' exist, both must pass
  if (condition.all && condition.all.length > 0 && condition.any && condition.any.length > 0) {
    return allPass && anyPass;
  }

  // If only 'all' exists, return allPass
  if (condition.all && condition.all.length > 0) {
    return allPass;
  }

  // If only 'any' exists, return anyPass
  if (condition.any && condition.any.length > 0) {
    return anyPass;
  }

  // If neither exists, return true
  return true;
};

/**
 * Evaluates a single condition against the form data
 */
const evaluateSingleCondition = (
  condition: Condition,
  formData: Record<string, any>
): boolean => {
  const value = formData[condition.questionId];

  switch (condition.operator) {
    case 'equals':
      return value === condition.value;

    case 'notEquals':
      return value !== condition.value;

    case 'includes':
      if (Array.isArray(value) && Array.isArray(condition.value)) {
        return condition.value.some((item) => value.includes(item));
      }
      if (Array.isArray(value)) {
        return value.includes(condition.value);
      }
      if (Array.isArray(condition.value)) {
        return condition.value.includes(value);
      }
      return false;

    case 'notIncludes':
      if (Array.isArray(value) && Array.isArray(condition.value)) {
        return !condition.value.some((item) => value.includes(item));
      }
      if (Array.isArray(value)) {
        return !value.includes(condition.value);
      }
      if (Array.isArray(condition.value)) {
        return !condition.value.includes(value);
      }
      return true;

    case 'isAnswered':
      return value !== undefined && value !== null && value !== '';

    case 'isNotAnswered':
      return value === undefined || value === null || value === '';

    default:
      return false;
  }
};

/**
 * Removes answers for questions that are no longer visible based on current form data
 * Returns new form data with hidden answers removed
 * Handles nested dependencies recursively
 * Preserves metadata fields that are not questionnaire questions
 */
export const cleanupHiddenAnswers = (
  sections: { questions: { id: string; visibleWhen?: VisibilityCondition }[] }[],
  formData: Record<string, any>
): Record<string, any> => {
  let newData = { ...formData };
  let hasChanges = true;
  
  // Keep cleaning until no more changes
  while (hasChanges) {
    hasChanges = false;
    
    // Collect all question IDs that should be visible
    const visibleQuestionIds = new Set<string>();
    
    for (const section of sections) {
      for (const question of section.questions) {
        if (evaluateVisibility(question.visibleWhen, newData)) {
          visibleQuestionIds.add(question.id);
          
          // Also check for related fields like 'other' fields
          if (question.id.includes('Comment')) {
            // Keep comment fields for visible questions
            visibleQuestionIds.add(question.id);
          }
        }
      }
    }
    
    // Remove answers for questions that are not visible
    // But preserve metadata fields that are not questionnaire questions
    const metadataFields = ['privacyConsent', 'crossBorderConsent', 'companyName', 'businessEmail'];
    
    for (const key in newData) {
      if (!visibleQuestionIds.has(key) && !metadataFields.includes(key)) {
        delete newData[key];
        hasChanges = true;
      }
    }
  }
  
  return newData;
};